// CP-248: one options object for vue-chartjs 4's <Line>.
//
// vue-chartjs 4 reads `chartOptions`, not `options`. LineChart used to send the caller's options (price
// formatting, label rotation, padding, tooltip) through `options`, which <Line> ignores, and only the theme text
// colour through `chartOptions`. This builds the single object it should get: defaults, then the caller's options,
// then the theme text colour (CP-225), merged deeply so a caller's `ticks.callback` and the theme's `ticks.color`
// both survive. The theme colour goes last so chart text keeps the contrast CP-225 measured in both themes.

export function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    && Object.prototype.toString.call(value) === '[object Object]';
}

// Deep merge of plain objects. Later layers win. Arrays, functions and other values are taken as they are
// (never merged element by element). Inputs are not modified.
export function mergeChartOptions(...layers) {
  const out = {};
  for (const layer of layers) {
    if (!isPlainObject(layer)) continue;
    for (const key of Object.keys(layer)) {
      const value = layer[key];
      if (isPlainObject(value)) {
        out[key] = mergeChartOptions(isPlainObject(out[key]) ? out[key] : {}, value);
      } else if (value !== undefined) {
        out[key] = value;
      }
    }
  }
  return out;
}

export const LINE_CHART_DEFAULTS = Object.freeze({
  responsive: true,
  maintainAspectRatio: false,
});

export function themeTextOptions(color) {
  return {
    scales: { x: { ticks: { color } }, y: { ticks: { color } } },
    plugins: { legend: { labels: { color } } },
  };
}

export function lineChartOptions(callerOptions, textColor) {
  return mergeChartOptions(LINE_CHART_DEFAULTS, callerOptions, themeTextOptions(textColor));
}

// CP-248: Detail chart at phone width (UI/UX, 390px). The canvas there is only ~286px tall, so every band that is not
// plot is taken back:
//   - the legend is hidden while one cabin type is shown (the INTERIOR panel above already names it) and kept when
//     "Show all cabin types" is on;
//   - date ticks are M/D without the year, horizontal (no rotation) and at most 7;
//   - the chart's top layout padding is 0 (UI/UX cap: 8px).
// Narrow = the same viewport query as the page's own ≤480px CSS, so markup, CSS and canvas switch together.
export const NARROW_CHART_WIDTH = 480;
export const NARROW_CHART_QUERY = `(max-width: ${NARROW_CHART_WIDTH}px)`;
export const NARROW_X_TICK_LIMIT = 7;
export const WIDE_X_TICK_LIMIT = 20;
export const CHART_TOP_PADDING = 0;
// Legend `labels.padding`. Chart.js puts this much space ABOVE the first legend row (and between items), so it is part
// of the button-to-chart gap; CruiseDetail's CSS takes it off the gap above the canvas (--chart-ink-inset).
export const LEGEND_LABEL_PADDING = 10;

export function detailXTickLimit(narrow) {
  return narrow ? NARROW_X_TICK_LIMIT : WIDE_X_TICK_LIMIT;
}

// '9/16/2026' (or any label Date can read) -> '9/16'. A label Date cannot read is returned unchanged.
export function shortDateTick(label) {
  if (label === null || label === undefined || label === '') return label;
  const d = new Date(label);
  if (Number.isNaN(d.getTime())) return label;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function detailLegendShown(narrow, allCabinTypesShown) {
  return !narrow || Boolean(allCabinTypesShown);
}

// The width-dependent part of the Detail chart options, merged over the base options.
export function detailResponsiveOptions({ narrow, allCabinTypesShown } = {}) {
  const xTicks = narrow
    ? {
      maxRotation: 0,
      minRotation: 0,
      maxTicksLimit: NARROW_X_TICK_LIMIT,
      // category scale: `value` is the label index; `this` is the scale
      callback(value) { return shortDateTick(this.getLabelForValue(value)); },
    }
    : { maxRotation: 45, minRotation: 45, maxTicksLimit: WIDE_X_TICK_LIMIT };
  return {
    layout: { padding: { top: CHART_TOP_PADDING } },
    scales: { x: { ticks: xTicks } },
    plugins: {
      legend: {
        display: detailLegendShown(narrow, allCabinTypesShown),
        labels: { padding: LEGEND_LABEL_PADDING },
      },
    },
  };
}

// CP-248 follow-up (10277, PO: never show cents; prices are whole dollars). On a flat series ($531-$532) Chart.js
// picked $0.10 steps and the whole-dollar `$` formatter printed "$532" x6 and "$531" x5. The y axis now:
//   - steps by whole dollars only: `precision: 0` makes Chart.js round its step UP to a whole number, so every step
//     is >= $1 and every tick value is a whole dollar. Normal ranges ($20 steps on 13402, $10 on 13400) come out
//     exactly as before, since their nice steps are already whole dollars.
//     Not `stepSize: 1`: in Chart.js 3 an explicit stepSize also sets the tick COUNT (one tick per $1, up to 1000)
//     instead of acting as a minimum, which would put a tick every $1-$5 on 13402 and 13400.
//   - keeps one tick per printed label (`afterBuildTicks`), so even a value the formatter rounds can never print
//     twice. A flat series may show as few as 2 ticks (e.g. $531 and $532).
export const WHOLE_DOLLAR_Y_TICKS = Object.freeze({ precision: 0 });

// First tick per label, in order. `format` maps a tick value to its printed label.
export function uniqueTicksByLabel(ticks, format) {
  if (!Array.isArray(ticks)) return ticks;
  const seen = new Set();
  const out = [];
  for (const tick of ticks) {
    const label = format(tick && tick.value);
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(tick);
  }
  return out;
}

// The y-axis options for a whole-dollar price axis. `format` is the tick label (Detail: formatPrice -> '$531').
export function wholeDollarYAxis(format) {
  return {
    ticks: {
      ...WHOLE_DOLLAR_Y_TICKS,
      callback(value) { return format(value); },
    },
    afterBuildTicks(scale) {
      scale.ticks = uniqueTicksByLabel(scale.ticks, format);
    },
  };
}

// CP-276: minimum y span on the Detail price chart, so a $1 move does not fill the plot like a cliff (10277: $531-$532
// drawn top to bottom). The y range is at least max($20, 5% of the highest price shown), centred on the data. A real
// range already wider than that is left exactly as Chart.js picks it. Only the axis moves: data, tooltips and prices
// are untouched. Chart.js then rounds the range out to its nice whole-dollar ticks (wholeDollarYAxis), so the shown
// span is the floor or a little more.
export const Y_SPAN_FLOOR_DOLLARS = 20;
export const Y_SPAN_FLOOR_SHARE = 0.05;

export function minYSpan(maxPrice) {
  return Math.max(Y_SPAN_FLOOR_DOLLARS, Y_SPAN_FLOOR_SHARE * (Number.isFinite(maxPrice) ? Math.abs(maxPrice) : 0));
}

// { min, max } of the data -> the axis range to use, or null to leave Chart.js's own range (no data, or the data
// already spans at least the floor). Never below $0 for positive prices.
export function yRangeWithFloor(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const floor = minYSpan(hi);
  if (hi - lo >= floor) return null;
  const centre = (lo + hi) / 2;
  let from = centre - floor / 2;
  let to = centre + floor / 2;
  if (lo >= 0 && from < 0) { to -= from; from = 0; }
  return { min: from, max: to };
}

// Finite y values of the visible datasets (null gaps and hidden cabin types skipped).
export function visibleYValues(chart) {
  const out = [];
  const datasets = (chart && chart.data && chart.data.datasets) || [];
  datasets.forEach((ds, i) => {
    if (chart.isDatasetVisible && !chart.isDatasetVisible(i)) return;
    for (const p of ds.data || []) {
      const v = p !== null && typeof p === 'object' ? p.y : p;
      if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
    }
  });
  return out;
}

// Detail y axis: whole-dollar ticks (CP-248) plus the CP-276 span floor, applied after Chart.js finds the data limits
// and before it builds ticks.
export function priceYAxis(format) {
  return {
    ...wholeDollarYAxis(format),
    afterDataLimits(scale) {
      const values = visibleYValues(scale.chart);
      if (!values.length) return;
      const range = yRangeWithFloor(Math.min(...values), Math.max(...values));
      if (!range) return;
      scale.min = range.min;
      scale.max = range.max;
    },
  };
}
